import React, { useState } from "react";
import {
  LocationClusterType,
  useGetAssetQuery,
  useGetChildrenAssetsQuery,
  useGetParentAssetsQuery,
} from "../store/slices/mapsSlice";
import {
  useGetContactFieldsQuery,
  useGetContactQuery,
} from "../store/slices/contactsSlice";
import ContactCard from "../Contacts/ContactCard";
import { Link } from "react-router-dom";
import {
  faAngleLeft,
  faAngleRight,
  faArrowUp,
  faCircle,
  faEdit,
  faEnvelope,
  faFilter,
  faWarning,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Offcanvas } from "react-bootstrap";
import {
  WeatherAlertConditions,
  WeatherAlertOperators,
  WeatherCodes,
  WindDirections,
} from "../WeatherAlerts/WeatherAlertConstants";
import LiveEventList from "../Events/LiveEventList";
import { EventDetailsTable } from "../Events/EventModal";
import MyModal from "../Common/MyModal";
import EditAssetModal from "../Assets/EditAssetModal";
import DependantSlider from "../Assets/DependantSlider";
import AssetContactsSlider from "../Assets/AssetContactsSlider";
import AssetsIcon from "../Assets/AssetsIcon";
import AssetKind from "../Assets/AssetKind";
import AssetLocationSlider from "../Assets/AssetLocationSlider";
import {
  useGetEventQuery,
  useGetEventsQuery,
} from "../store/slices/settingsSlice";
import CreateEventModal from "../Events/CreateEventModal";

export function ClusterPopUp({ cluster }: { cluster: LocationClusterType }) {
  return (
    <div className="">
      <table className="table">
        <tbody>
          <tr>
            <td>Locations:</td>
            <td className="text-end ps-3">{cluster.ItemCount}</td>
          </tr>
          {cluster.ActiveEventsSeverity && (
            <>
              <tr>
                <br />
              </tr>
              <tr>
                <th>Active Events</th>
                <td></td>
              </tr>
              {Object.entries(cluster.ActiveEventsSeverity).map(
                ([key, value]) => (
                  <tr key={key}>
                    <td className="text-capitalize">{key} Severity:</td>
                    <td className="text-end ps-3">{value}</td>
                  </tr>
                )
              )}
            </>
          )}
          {cluster.ActiveEventAssetsCount &&
            cluster.ActiveEventAssetsCount > 0 && (
              <tr>
                <td>Assets Affected:</td>
                <td className="text-end ps-3">
                  {cluster.ActiveEventAssetsCount}
                </td>
              </tr>
            )}
        </tbody>
      </table>
    </div>
  );
}

export function AssetOverlay({
  assetUuid,
  assetOnly,
  showEvents,
  cluster,
}: {
  assetUuid: string;
  assetOnly?: boolean;
  showEvents?: boolean;
  cluster?: LocationClusterType;
}) {
  const { data: asset, isLoading } = useGetAssetQuery(assetUuid, {});
  const [selectedTab, setSelectedTab] = useState("details");
  const { data: { events, total } = { events: [], total: 0 } } =
    useGetEventsQuery(
      { page: 0, perPage: 1, assets: [assetUuid], status: "active" }
      // { skip: !showEvents }
    );
  const { data: ParentAssets } = useGetParentAssetsQuery(assetUuid);
  const { data: ChildAssets } = useGetChildrenAssetsQuery(assetUuid);

  if (isLoading) {
    return (
      <div className="bg-white p-3 rounded-3 mb-1">
        <div className="spinner-border" role="status"></div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "400px", overflowY: "auto" }}>
      {!showEvents ? (
        <>
          <h2 className="d-flex align-items-center gap-2">
            <AssetsIcon assetKind={asset?.Kind} width={28} />
            {asset?.Label}
          </h2>
          <table className="table flushed table-transparent">
            <tbody className="">
              <tr>
                <td>Kind</td>
                <td className="text-end">
                  <AssetKind assetKind={asset?.Kind} />
                </td>
              </tr>
              <tr>
                <td>Description</td>
                <td className="text-break text-end">{asset?.Description}</td>
              </tr>
              <tr>
                <td>Reference</td>
                <td className="text-end">{asset?.Reference}</td>
              </tr>
              <tr>
                <td>Address</td>
                <td className="text-end">{asset?.Address}</td>
              </tr>
              <tr>
                <td>Created at</td>
                <td className="text-end">
                  {asset?.CreatedAt
                    ? new Date(asset?.CreatedAt).toLocaleString()
                    : "N/A"}
                </td>
              </tr>
              <tr>
                <td className="">
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="bg-transparent p-0 small"
                  >
                    <AssetLocationSlider asset={asset} />
                  </div>
                </td>
                <td className="text-end">
                  {asset?.HasLocation ? (
                    <span className="badge text-bg-success rounded-pill">
                      {asset?.Stats?.Locations}
                    </span>
                  ) : (
                    <span className="badge text-bg-danger rounded-pill">
                      N/A
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <td className="">
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="bg-transparent p-0"
                  >
                    <AssetContactsSlider asset={asset} />
                  </div>
                </td>
                <td className="text-end">{asset?.Stats?.Contacts}</td>
              </tr>
              <tr>
                <td className="">
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="bg-transparent p-0"
                  >
                    <DependantSlider
                      assetUuid={asset?.Uuid}
                      relationType="connected"
                    />
                  </div>
                </td>
                <td className="text-end">
                  {!!asset?.Stats?.Parents && (
                    <Link
                      to="/assets/maps"
                      title="Filter connected assets in the map"
                      className="p-0 bg-transparent"
                      state={{
                        tab: "assets-contacts",
                        mapFilters: {
                          AssetUuids: ParentAssets?.assets?.map((a) => a.Uuid),
                          Assets: true,
                          Contacts: false,
                          OnlyItemsWithEvents: false,
                          EventsUuids: [],
                          weatherFilters: {
                            isWeather: false,
                            showGrids: false,
                            StartAt: "",
                            EndAt: "",
                            range: -1,
                            period: "daily",
                            alertFilters: {
                              ruleID: undefined,
                              conditionIds: [],
                              conditions: [],
                              search: "",
                            },
                          },
                          eventsFilters: { isEvent: false },
                        },
                      }}
                    >
                      <FontAwesomeIcon icon={faFilter} className="mx-2" />
                    </Link>
                  )}
                  {asset?.Stats?.Parents}
                </td>
              </tr>
              <tr>
                <td className="">
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="bg-transparent p-0"
                  >
                    <DependantSlider
                      assetUuid={asset?.Uuid}
                      relationType="dependant"
                    />
                  </div>
                </td>
                <td className="text-end">
                  {!!asset?.Stats?.Children && (
                    <Link
                      to="/assets/maps"
                      title="Filter dependant assets in the map"
                      className="p-0 bg-transparent"
                      state={{
                        tab: "assets-contacts",
                        mapFilters: {
                          AssetUuids: ChildAssets?.assets?.map((a) => a.Uuid),
                          Assets: true,
                          Contacts: false,
                          OnlyItemsWithEvents: false,
                          EventsUuids: [],
                          weatherFilters: {
                            isWeather: false,
                            showGrids: false,
                            StartAt: "",
                            EndAt: "",
                            range: -1,
                            period: "daily",
                            alertFilters: {
                              ruleID: undefined,
                              conditionIds: [],
                              conditions: [],
                              search: "",
                            },
                          },
                          eventsFilters: { isEvent: false },
                        },
                      }}
                    >
                      <FontAwesomeIcon icon={faFilter} className="mx-2" />
                    </Link>
                  )}
                  {asset?.Stats?.Children}
                </td>
              </tr>
              {assetOnly && !!asset?.Stats?.ActiveEvents && (
                <tr>
                  <td>
                    <Link
                      title="Go to events view"
                      className={`p-0 bg-transparent`}
                      to={`/assets/maps`}
                      state={{
                        tab: "events",
                        mapFilters: {
                          Assets: true,
                          Contacts: false,
                          AssetUuids: [],
                          EventsUuids: events?.map((e) => e.Uuid) || [],
                          OnlyItemsWithEvents: true,
                          eventsFilters: {
                            isEvent: true,
                          },
                          weatherFilters: {
                            isWeather: false,
                            showGrids: false,
                            StartAt: "",
                            EndAt: "",
                            range: -1,
                            period: "daily",
                            alertFilters: {
                              ruleID: undefined,
                              conditionIds: [],
                              conditions: [],
                              search: "",
                            },
                          },
                        },
                      }}
                    >
                      Active Events
                      <FontAwesomeIcon
                        icon={faWarning}
                        style={{
                          color: cluster?.ActiveEventsSeverity?.high
                            ? "red"
                            : cluster?.ActiveEventsSeverity?.medium
                            ? "orange"
                            : cluster?.ActiveEventsSeverity?.low
                            ? "gold"
                            : "",
                        }}
                      />
                    </Link>
                  </td>
                  <td className="text-end">{asset?.Stats?.ActiveEvents}</td>
                </tr>
              )}
              <tr>
                <td colSpan={2} className="m-0 p-0">
                  <div onClick={(e) => e.stopPropagation()}>
                    <EditAssetModal
                      assetUuid={assetUuid}
                      tab="details"
                      modalButton={
                        <span className="btn btn-sm btn-link p-2">
                          View Asset
                        </span>
                      }
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </>
      ) : (
        <>
          <h2 className="d-flex justify-content-between align-items-center">
            <AssetsIcon assetKind={asset?.Kind} />
            {asset?.Label}
          </h2>
          <table className="table flushed">
            <tbody>
              <tr>
                <td>Kind</td>
                <td className="text-end">
                  <AssetKind assetKind={asset?.Kind} />
                </td>
              </tr>
              <tr>
                <td>Description</td>
                <td className="text-break text-end">{asset?.Description}</td>
              </tr>
              <tr>
                <td>Reference</td>
                <td className="text-end">{asset?.Reference}</td>
              </tr>
            </tbody>
          </table>
          {events?.[0] && (
            <>
              <h5>Most recent event</h5>
              <EventDetailsTable eventUuid={events[0]?.Uuid} />
            </>
          )}
        </>
      )}

      <div onClick={(e) => e.stopPropagation()} className="p-2">
        <CreateEventModal
          showButton={
            <button className="btn btn-link btn-sm p-0 border-0 w-100 text-start">
              Create event
            </button>
          }
          initialData={{
            Assets: [assetUuid],
            ContactsAndAssetsCriteria: {
              IncludeAssets: [assetUuid],
              ExcludeAssets: [],
              UseAssets: true,
            },
          }}
          from="asset"
        />
      </div>
    </div>
  );
}

export function EventOverlay({
  eventUuid,
  cluster,
}: {
  eventUuid: string;
  cluster?: LocationClusterType;
}) {
  return (
    <div style={{ maxWidth: "400px", maxHeight: "400px", overflowY: "auto" }}>
      <EventDetailsTable eventUuid={eventUuid} />
    </div>
  );
}

export function ContactOverlay({ contactUuid }: { contactUuid: string }) {
  const { data: contact } = useGetContactQuery({
    contactUuid,
    isAllFields: true,
    hideToasts: true,
  });
  const { data: fields } = useGetContactFieldsQuery();

  return contact ? (
    <div className="bg-white p-3 rounded-3">
      <table className="table mb-0">
        <tbody>
          <ContactCard
            contact={contact}
            contactButton={() => (
              <>
                <Link
                  to={`/assets/contacts/edit/${contactUuid}`}
                  className="me-2"
                  title="Edit contact"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </Link>
                <Link
                  title="Send broadcast"
                  to="/library/broadcast/new/settings"
                  state={{
                    contacts: [contactUuid],
                  }}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                </Link>
              </>
            )}
            contactFields={fields}
            fullView={undefined}
            isModal={undefined}
            onSelect={undefined}
            showContactFields={undefined}
            isSelected={undefined}
            highlighChannelsIfNotAvailable={undefined}
          />
        </tbody>
      </table>
    </div>
  ) : (
    <div className="alert alert-secondary m-0">Contact not found</div>
  );
}

function showConditionDetails(cd: any, i: number) {
  const condition = WeatherAlertConditions.find(
    (wac) => wac.key === cd.Condition
  );
  const operator = WeatherAlertOperators.find(
    (wao) => wao.symbol === cd.ConditionOperator
  );
  if (!condition || !operator) return null;
  let ConditionIcon = condition.icon;
  let label = condition.label;
  let values = cd.ConditionValue;
  let weatherCodes: any[] = [];
  if (
    condition?.key === "daySignificantWeatherCode" ||
    condition?.key === "nightSignificantWeatherCode"
  ) {
    weatherCodes = values.map((v: any) =>
      WeatherCodes.find((wc) => wc.value === v)
    );
  }

  if (
    condition?.key === "midday10MWindDirection" ||
    condition?.key === "midnight10MWindDirection"
  ) {
    weatherCodes = values.map((v: any) => {
      const weatherCode = WindDirections.find((wc) => wc.degrees === v);
      return {
        label: (
          <>
            <FontAwesomeIcon
              icon={faArrowUp}
              transform={{ rotate: weatherCode?.degrees }}
            />{" "}
            {weatherCode?.direction}
          </>
        ),
        icon: null,
      };
    });
  }

  if (
    condition?.key === "midday10MWindSpeed" ||
    condition?.key === "midnight10MWindSpeed"
  ) {
    weatherCodes = values.map((v: any) => {
      return {
        label: v + condition?.unit,
        icon: null,
      };
    });
  }

  if (
    condition?.key === "dayMaxScreenTemperature" ||
    condition?.key === "nightMinScreenTemperature"
  ) {
    weatherCodes = values.map((v: any) => {
      return {
        label: v + condition?.unit,
        icon: null,
      };
    });
  }

  if (
    condition?.key === "midnightVisibility" ||
    condition?.key === "middayVisibility"
  ) {
    weatherCodes = values.map((v: any) => {
      return {
        label: v + condition?.unit,
        icon: null,
      };
    });
  }
  if (
    condition?.key === "dayProbabilityOfSnow" ||
    condition?.key === "nightProbabilityOfSnow"
  ) {
    weatherCodes = values.map((v: any) => {
      return {
        label: v + condition?.unit,
        icon: null,
      };
    });
  }

  return (
    <div key={i} className="d-flex align-items-center mb-2">
      <div>
        <ConditionIcon className="me-2" width={30} />
      </div>
      {label} {operator?.desc}{" "}
      <div className="d-flex flex-wrap">
        {weatherCodes.length > 0
          ? weatherCodes.map((wc) => {
              return (
                <span className="badge bg-light text-dark me-2 mb-1 rounded-2">
                  {wc.icon && <wc.icon width={30} />} {wc.label}
                </span>
              );
            })
          : values.join(", ")}
      </div>
    </div>
  );
}

export function WeatherAlertOverlay({
  data,
  type,
}: {
  data: any;
  type: "alert" | "cluster";
}) {
  let grid;

  if (type === "alert") {
    grid = data.Grid;
  } else if (type === "cluster") {
    grid = data.Item.Grid;
  }

  const items = data.Items || [data];

  const [showSidebar, setShowSidebar] = useState(false);
  return (
    <>
      <div style={{ maxWidth: "600px" }}>
        {data.Count > 1 ? (
          <div>{data.Count} alerts found in this area</div>
        ) : (
          ""
        )}
        <div className="p-2">
          {data.Unit?.Name ||
            data.Shape?.Name ||
            data.Item.Unit?.Name ||
            data.Item.Shape?.Name}
          {(() => {
            const a = grid[0].Alerts[0];
            return (
              <div key={a.Id}>
                <div>
                  <FontAwesomeIcon
                    icon={faCircle}
                    color={a.AlertCondition.Color}
                    className="me-2"
                  />{" "}
                  {a.Message} {new Date(a.StartAt).toLocaleDateString()}
                </div>
                <div className="text-nowrap">
                  {a.AlertCondition.ConditionDetails.map(showConditionDetails)}
                </div>
              </div>
            );
          })()}
        </div>
        <div>
          <button
            className="btn btn-link p-0"
            onClick={() => setShowSidebar(true)}
          >
            See alerts
          </button>
        </div>
      </div>
      <Offcanvas
        show={showSidebar}
        onHide={() => setShowSidebar(false)}
        // onHide={() => setOverlay({ show: false, content: undefined })}
        style={{ width: "50%" }}
        placement="end"
        className="z-3000"
        backdropClassName="z-2999"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            Weather Alerts
            {data.Count ? ` - ${data.Count} alerts in this area` : ""}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {items.map((item: any, idx: number) => (
            <div className="mb-4" key={idx}>
              {item.Grid.map((d: any, i: number) => (
                <div key={i}>
                  <h5>Weather alerts for {d.Shape.Name}</h5>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>Description</th>
                        <th>Condition</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.Alerts.map((a: any) => (
                        <tr key={a.Id}>
                          <td>{new Date(a.StartAt).toLocaleDateString()}</td>
                          <td>{a.Message}</td>
                          <td>
                            <FontAwesomeIcon
                              icon={faCircle}
                              color={a.AlertCondition.Color}
                              className="me-2"
                            />
                            {a.AlertCondition.ConditionDetails.map(
                              showConditionDetails
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ))}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

// You can place this in a file like types.ts

export type WeatherForecastEntry = {
  time: string;
  feelsLikeTemp: number;
  mslp: number;
  max10mWindGust: number;
  maxScreenAirTemp: number;
  minScreenAirTemp: number;
  probOfHail: number;
  probOfHeavyRain: number;
  probOfHeavySnow: number;
  probOfPrecipitation: number;
  probOfRain: number;
  probOfSferics: number;
  probOfSnow: number;
  screenRelativeHumidity: number;
  significantWeatherCode: number;
  totalPrecipAmount: number;
  totalSnowAmount: number;
  uvIndex: number;
  visibility: number;
  windDirectionFrom10m: number;
  windGustSpeed10m: number;
  windSpeed10m: number;
};

export type WeatherForecastItem = {
  Point: [number, number];
  LocationName: string;
  CountryIso: string;
  Weather: {
    [isoDate: string]: WeatherForecastEntry;
  };
};

export type WeatherForecastClusterType = {
  X: number;
  Y: number;
  IsCluster: boolean;
  ItemCount: number;
  Items: WeatherForecastItem[];
};

export function WeatherForecastOverlay({
  data,
  date = "",
  clientUnits,
}: {
  data: WeatherForecastClusterType;
  date?: string;
  clientUnits?: any;
}) {
  const items = data.Items || [data];

  const [showSidebar, setShowSidebar] = useState(false);
  const [clusterIndex, setClusterIndex] = useState(0);

  return (
    <>
      <div style={{ maxWidth: "600px" }}>
        {data.ItemCount > 1 ? (
          <div>
            {data.ItemCount} weather forecasts found in this area
            <div className="d-flex justify-content-between align-items-center">
              <button
                className="btn btn-outline-secondary border-0 btn-sm me-2"
                disabled={clusterIndex <= 0}
                onClick={() => setClusterIndex((idx) => Math.max(0, idx - 1))}
                title="Previous"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <span>
                {clusterIndex + 1} / {data.ItemCount}
              </span>
              <button
                className="btn btn-outline-secondary border-0 btn-sm ms-2"
                disabled={clusterIndex >= data.ItemCount - 1}
                onClick={() =>
                  setClusterIndex((idx) =>
                    Math.min(data.ItemCount - 1, idx + 1)
                  )
                }
                title="Next"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          </div>
        ) : (
          ""
        )}

        <table className="table table-sm mb-3 bg-purple-gradient">
          <tbody>
            <tr>
              <th>Location</th>
              <td>{data.Items[clusterIndex]?.LocationName ?? "N/A"}</td>
            </tr>
            <tr>
              <th>Country</th>
              <td>{data.Items[clusterIndex]?.CountryIso ?? "N/A"}</td>
            </tr>
            <tr>
              <th>Time</th>
              <td>
                {data.Items[clusterIndex]?.Weather
                  ? new Date(date).toLocaleString()
                  : "N/A"}
              </td>
            </tr>
            <tr>
              <th>Min Temperature</th>
              <td>
                {Math.round(
                  data.Items[clusterIndex]?.Weather
                    ? data.Items[clusterIndex].Weather[date]
                        ?.minScreenAirTemp ?? 0
                    : 0
                )}{" "}
                {clientUnits?.temperature || "°C"}
              </td>
            </tr>
            <tr>
              <th>Max Temperature</th>
              <td>
                {Math.round(
                  data.Items[clusterIndex]?.Weather
                    ? data.Items[clusterIndex].Weather[date]
                        ?.maxScreenAirTemp ?? 0
                    : 0
                )}{" "}
                {clientUnits?.temperature || "°C"}
              </td>
            </tr>
            <tr>
              <th>Feels Like</th>
              <td>
                {Math.round(
                  data.Items[clusterIndex]?.Weather
                    ? data.Items[clusterIndex].Weather[date]?.feelsLikeTemp ?? 0
                    : 0
                )}{" "}
                {clientUnits?.temperature || "°C"}
              </td>
            </tr>
            <tr>
              <th>Humidity</th>
              <td>
                {Math.round(
                  data.Items[clusterIndex]?.Weather
                    ? data.Items[clusterIndex].Weather[date]
                        ?.screenRelativeHumidity ?? 0
                    : 0
                )}
                %
              </td>
            </tr>
            <tr>
              <th>Wind Speed</th>
              <td>
                {Math.round(
                  data.Items[clusterIndex]?.Weather
                    ? data.Items[clusterIndex].Weather[date]?.windSpeed10m ?? 0
                    : 0
                )}{" "}
                {clientUnits?.speed || "m/s"}
              </td>
            </tr>
            <tr>
              <th>Weather Code</th>
              <td>
                {WeatherCodes.find(
                  (wac) =>
                    wac.value ===
                    (data.Items[clusterIndex]?.Weather
                      ? data.Items[clusterIndex].Weather[date]
                          ?.significantWeatherCode ?? 0
                      : 0)
                )?.label ?? "N/A"}{" "}
                (code{" "}
                {data.Items[clusterIndex]?.Weather
                  ? data.Items[clusterIndex].Weather[date]
                      ?.significantWeatherCode ?? 0
                  : 0}
                )
              </td>
            </tr>
          </tbody>
        </table>
        <div>
          <button
            className="btn btn-link p-0"
            onClick={() => setShowSidebar(true)}
          >
            See more
          </button>
        </div>
      </div>
      <Offcanvas
        show={showSidebar}
        onHide={() => setShowSidebar(false)}
        // onHide={() => setOverlay({ show: false, content: undefined })}
        style={{ width: "50%" }}
        placement="end"
        className="z-3000"
        backdropClassName="z-2999"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            Weather Forecast
            {data.IsCluster
              ? ` - ${data.ItemCount} weather forecasts in this area`
              : ""}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {items.map((item, idx: number) => (
            <div className="mb-4" key={idx}>
              <h5>Weather forecast for {item.LocationName}</h5>
              {Object.keys(item.Weather).map((d) => (
                <table className="table table-sm mb-3">
                  <tbody>
                    <tr>
                      <th>Time</th>
                      <td>{new Date(d).toLocaleString()}</td>
                    </tr>
                    <tr>
                      <th>Temperature</th>
                      <td>
                        {Math.round(item.Weather[d]?.minScreenAirTemp ?? 0)} -{" "}
                        {Math.round(item.Weather[d]?.maxScreenAirTemp ?? 0)}{" "}
                        {clientUnits?.temperature || "°C"}
                      </td>
                    </tr>
                    <tr>
                      <th>Feels Like</th>
                      <td>
                        {Math.round(item.Weather[d]?.feelsLikeTemp ?? 0)}{" "}
                        {clientUnits?.temperature || "°C"}
                      </td>
                    </tr>
                    <tr>
                      <th>Humidity</th>
                      <td>
                        {Math.round(
                          item.Weather[d]?.screenRelativeHumidity ?? 0
                        )}
                        %
                      </td>
                    </tr>
                    <tr>
                      <th>Wind Speed</th>
                      <td>
                        {Math.round(item.Weather[d]?.windSpeed10m ?? 0)}{" "}
                        {clientUnits?.speed || "m/s"}
                      </td>
                    </tr>
                    <tr>
                      <th>Wind Direction</th>
                      <td>{item.Weather[d]?.windDirectionFrom10m ?? 0}°</td>
                    </tr>
                    <tr>
                      <th>Wind Gust</th>
                      <td>
                        {Math.round(item.Weather[d]?.windGustSpeed10m ?? 0)}{" "}
                        {clientUnits?.speed || "m/s"}
                      </td>
                    </tr>
                    <tr>
                      <th>Max Wind Gust</th>
                      <td>
                        {Math.round(item.Weather[d]?.max10mWindGust ?? 0)}{" "}
                        {clientUnits?.speed || "m/s"}
                      </td>
                    </tr>
                    <tr>
                      <th>Visibility</th>
                      <td>
                        {item.Weather[d]?.visibility ?? 0}{" "}
                        {clientUnits?.length || "m"}
                      </td>
                    </tr>
                    <tr>
                      <th>Pressure (mslp)</th>
                      <td>
                        {item.Weather[d]?.mslp ?? 0}{" "}
                        {clientUnits?.pressure || "hPa"}
                      </td>
                    </tr>
                    <tr>
                      <th>UV Index</th>
                      <td>{item.Weather[d]?.uvIndex ?? 0}</td>
                    </tr>
                    <tr>
                      <th>Weather Code</th>
                      <td>
                        {
                          WeatherCodes.find(
                            (wac) =>
                              wac.value ===
                              (item.Weather[d]?.significantWeatherCode ?? 0)
                          )?.label
                        }{" "}
                        (code {item.Weather[d]?.significantWeatherCode ?? 0})
                      </td>
                    </tr>
                    <tr>
                      <th>Total Precipitation</th>
                      <td>
                        {Math.round(item.Weather[d]?.totalPrecipAmount ?? 0)}{" "}
                        {clientUnits?.precipitation || "mm"}
                      </td>
                    </tr>
                    <tr>
                      <th>Total Snow</th>
                      <td>
                        {item.Weather[d]?.totalSnowAmount ?? 0}{" "}
                        {clientUnits?.precipitation || "mm"}
                      </td>
                    </tr>
                    <tr>
                      <th>Probability of Precipitation</th>
                      <td>{item.Weather[d]?.probOfPrecipitation ?? 0}%</td>
                    </tr>
                    <tr>
                      <th>Probability of Snow</th>
                      <td>{item.Weather[d]?.probOfSnow ?? 0}%</td>
                    </tr>
                    <tr>
                      <th>Probability of Heavy Snow</th>
                      <td>{item.Weather[d]?.probOfHeavySnow ?? 0}%</td>
                    </tr>
                    <tr>
                      <th>Probability of Rain</th>
                      <td>{item.Weather[d]?.probOfRain ?? 0}%</td>
                    </tr>
                    <tr>
                      <th>Probability of Heavy Rain</th>
                      <td>{item.Weather[d]?.probOfHeavyRain ?? 0}%</td>
                    </tr>
                    <tr>
                      <th>Probability of Hail</th>
                      <td>{item.Weather[d]?.probOfHail ?? 0}%</td>
                    </tr>
                    <tr>
                      <th>Probability of Sferics</th>
                      <td>{item.Weather[d]?.probOfSferics ?? 0}%</td>
                    </tr>
                  </tbody>
                </table>
              ))}
            </div>
          ))}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}
